/**
 * =============================================================================
 * SHARY SEASONAL ENGINE — Core Runtime (AUDITED 2026-01-05)
 * =============================================================================
 * Manages the seasonal atmosphere system with requestAnimationFrame,
 * visibility handling, and performance optimization.
 * 
 * PHILOSOPHY: Luxury through restraint.
 * - Effects must feel cinematic, calm, intentional
 * - Pause on document.hidden
 * - Low-end devices: force intensity 1 + subtle style
 * - FAIL LOUD, NOT SILENT (audit requirement)
 * 
 * DEBUG: Add ?debugSeasonal=1 to URL for diagnostics panel
 * AUDIT: Add ?auditSeasonal=1 for hard visual assertions
 * 
 * DEPENDS ON: shary-seasonal-presets.js (must load first)
 * =============================================================================
 */

(function() {
  'use strict';

  // ---------------------------------------------------------------------------
  // CONSTANTS
  // ---------------------------------------------------------------------------
  
  const STORAGE_KEY = 'shary_seasonal_enabled_v1';
  const EID_INTRO_KEY = 'shary_eid_intro_shown_v1';
  const TOOLTIP_TEXT = 'مؤثرات موسمية';
  
  // ---------------------------------------------------------------------------
  // DEBUG & AUDIT MODES
  // ---------------------------------------------------------------------------
  
  const urlParams = new URLSearchParams(window.location.search);
  const DEBUG_MODE = urlParams.get('debugSeasonal') === '1';
  const AUDIT_MODE = urlParams.get('auditSeasonal') === '1';
  
  // ---------------------------------------------------------------------------
  // AUDIT SYSTEM — HARD VISUAL ASSERTIONS (PHASE 1)
  // ---------------------------------------------------------------------------
  
  const auditState = {
    loaderFound: false,
    overlayAttached: false,
    presetInitCalled: false,
    presetStartCalled: false,
    reducedMotionActive: false,
    scopeMatched: false,
    errors: []
  };
  
  /**
   * Show a hard visual assertion overlay — CANNOT BE MISSED
   * @param {string} message - Error message
   * @param {'error'|'warning'|'success'} type - Type affects color
   */
  function showAuditOverlay(message, type = 'error') {
    const colors = {
      error: { bg: 'color-mix(in srgb, var(--shary-error) 95%, transparent)', border: 'var(--shary-error)', text: 'var(--shary-card-bg)' },
      warning: { bg: 'color-mix(in srgb, var(--shary-warning) 95%, transparent)', border: 'var(--shary-warning)', text: 'var(--shary-text-primary)' },
      success: { bg: 'color-mix(in srgb, var(--shary-success) 95%, transparent)', border: 'var(--shary-success)', text: 'var(--shary-card-bg)' }
    };
    const c = colors[type] || colors.error;
    
    const overlay = document.createElement('div');
    overlay.className = 'shary-audit-overlay';
    overlay.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      z-index: 999999 !important;
      background: ${c.bg} !important;
      border: 8px solid ${c.border} !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-direction: column !important;
      padding: 40px !important;
      box-sizing: border-box !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
      pointer-events: auto !important;
    `;
    
    const title = document.createElement('div');
    title.style.cssText = `
      font-size: 32px !important;
      font-weight: bold !important;
      color: ${c.text} !important;
      margin-bottom: 20px !important;
      text-align: center !important;
    `;
    title.textContent = type === 'error' ? '❌ SEASONAL FAIL' : type === 'warning' ? '⚠️ SEASONAL WARNING' : '✅ SEASONAL OK';
    
    const msg = document.createElement('div');
    msg.style.cssText = `
      font-size: 24px !important;
      color: ${c.text} !important;
      text-align: center !important;
      max-width: 600px !important;
      line-height: 1.4 !important;
    `;
    msg.textContent = message;
    
    const dismissBtn = document.createElement('button');
    dismissBtn.style.cssText = `
      margin-top: 30px !important;
      padding: 15px 40px !important;
      font-size: 18px !important;
      background: ${c.text} !important;
      color: ${c.bg.replace('0.95', '1')} !important;
      border: none !important;
      border-radius: 8px !important;
      cursor: pointer !important;
    `;
    dismissBtn.textContent = 'Dismiss (Continue Audit)';
    dismissBtn.addEventListener('click', () => overlay.remove());
    
    overlay.appendChild(title);
    overlay.appendChild(msg);
    overlay.appendChild(dismissBtn);
    
    document.body.appendChild(overlay);
    
    // Also log to console
    console.error(`[SHARY AUDIT] ${type.toUpperCase()}: ${message}`);
  }
  
  /**
   * Show execution trace panel (PHASE 3)
   */
  function showTracePanel(data) {
    // Remove existing panel if any
    const existing = document.getElementById('shary-audit-trace');
    if (existing) existing.remove();
    
    const panel = document.createElement('div');
    panel.id = 'shary-audit-trace';
    panel.style.cssText = `
      position: fixed !important;
      top: 10px !important;
      left: 10px !important;
      z-index: 99998 !important;
      background: color-mix(in srgb, var(--shary-text-primary) 95%, transparent) !important;
      color: var(--shary-success) !important;
      font-family: 'Courier New', monospace !important;
      font-size: 11px !important;
      padding: 12px !important;
      border-radius: 8px !important;
      border: 2px solid var(--shary-success) !important;
      max-width: 320px !important;
      box-shadow: var(--shary-card-shadow) !important;
      pointer-events: auto !important;
    `;
    
    const title = document.createElement('div');
    title.style.cssText = 'font-size: 14px; font-weight: bold; margin-bottom: 10px; color: var(--shary-orange); border-bottom: 1px solid color-mix(in srgb, var(--shary-text-primary) 30%, transparent); padding-bottom: 8px;';
    title.textContent = '🔍 SEASONAL TRACE PANEL';
    panel.appendChild(title);
    
    const rows = [
      { label: 'Page Type', value: data.pageType, ok: true },
      { label: 'Scope Setting', value: data.scope, ok: data.scopeMatched },
      { label: 'Scope Matched', value: data.scopeMatched, ok: data.scopeMatched },
      { label: 'Season', value: data.season, ok: !!data.season },
      { label: 'Intensity', value: data.intensity, ok: data.intensity >= 1 && data.intensity <= 3 },
      { label: 'localStorage', value: data.localStorage, ok: data.localStorage !== 'false' },
      { label: 'Preset Name', value: data.presetName, ok: !!data.presetName },
      { label: 'Particle Count', value: data.particleCount, ok: data.particleCount > 0 },
      { label: 'Canvas CSS Size', value: data.canvasCssSize, ok: data.canvasCssOk },
      { label: 'Canvas Internal', value: data.canvasInternalSize, ok: data.canvasInternalOk },
      { label: 'Canvas DPR', value: data.canvasDpr, ok: data.canvasDpr >= 1 },
      { label: 'Overlay z-index', value: data.overlayZ, ok: data.overlayZ >= 50 },
      { label: 'Loader Found', value: data.loaderFound, ok: data.loaderFound },
      { label: 'Overlay Attached', value: data.overlayAttached, ok: data.overlayAttached },
      { label: 'Reduced Motion', value: data.reducedMotion, ok: !data.reducedMotion }
    ];
    
    rows.forEach(row => {
      const line = document.createElement('div');
      line.style.cssText = `margin: 4px 0; display: flex; justify-content: space-between;`;
      
      const label = document.createElement('span');
      label.textContent = row.label + ':';
      label.style.color = 'var(--shary-text-muted)';
      
      const statusText = row.ok === undefined ? '' : (row.ok ? 'PASS' : 'FAIL');

      const status = document.createElement('span');
      status.textContent = statusText;
      status.style.cssText = `margin-left: 6px; font-weight: bold; color: ${row.ok ? 'var(--shary-success)' : 'var(--shary-error)'};`;

      const value = document.createElement('span');
      value.textContent = String(row.value);
      value.style.cssText = `font-weight: bold; color: ${row.ok ? 'var(--shary-success)' : 'var(--shary-error)'}; margin-left: 6px;`;
      
      if (row.value === null || row.value === undefined || row.value === '') {
        value.textContent = 'NULL';
        value.style.color = 'var(--shary-error)';
        value.style.backgroundColor = 'color-mix(in srgb, var(--shary-error) 20%, transparent)';
        value.style.padding = '0 4px';
      }
      
      line.appendChild(label);
      line.appendChild(status);
      line.appendChild(value);
      panel.appendChild(line);
    });
    
    // Add close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕ Close';
    closeBtn.style.cssText = 'margin-top: 10px; padding: 5px 10px; background: var(--shary-text-primary); color: var(--shary-card-bg); border: 1px solid var(--shary-divider-light); border-radius: 4px; cursor: pointer; width: 100%;';
    closeBtn.addEventListener('click', () => panel.remove());
    panel.appendChild(closeBtn);
    
    document.body.appendChild(panel);
  }
  
  function debugLog(...args) {
    if (DEBUG_MODE || AUDIT_MODE) {
      console.log('[Shary Seasonal Debug]', ...args);
    }
  }
  
  function debugWarn(...args) {
    if (DEBUG_MODE || AUDIT_MODE) {
      console.warn('[Shary Seasonal Debug]', ...args);
    }
  }
  
  function debugError(...args) {
    if (DEBUG_MODE || AUDIT_MODE) {
      console.error('[Shary Seasonal Debug]', ...args);
    }
  }

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  
  let state = {
    initialized: false,
    active: true,
    season: null,
    intensity: 2,
    container: null,
    toggleBtn: null,
    particles: [],
    rafId: null,
    paused: false,
    eidIntroShown: false,
    debugPanel: null,
    boostMode: false,
    // Config from theme settings
    themeEnabled: false,
    scope: 'all',
    style: 'default',
    currentPreset: null,
    canvas: null,
    ctx: null,
    canvasParticles: [],
    canvasSize: { width: 0, height: 0, dpr: 1 },
    resizeObserver: null,
    sizeCheckAttempts: 0,
    lastFrameTime: 0,
    resizeHandler: null,
    orientationHandler: null
  };

  // ---------------------------------------------------------------------------
  // UTILITY FUNCTIONS
  // ---------------------------------------------------------------------------
  
  /**
   * Check if reduced motion is preferred
   */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Get random number in range
   */
  function randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  /**
   * Get random integer in range
   */
  function randomInt(min, max) {
    return Math.floor(randomInRange(min, max + 1));
  }

  /**
   * Load saved preference from localStorage
   */
  function loadPreference() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      debugLog('localStorage value:', saved);
      if (saved !== null) {
        return saved === 'true';
      }
    } catch (e) {
      debugWarn('localStorage read failed:', e);
    }
    return true; // Default: enabled
  }

  /**
   * Get stored preference as string for debug (AUDIT helper)
   */
  function getStoredPreference() {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'null (default: true)';
    } catch (e) {
      return 'blocked';
    }
  }

  /**
   * Save preference to localStorage
   */
  function savePreference(enabled) {
    try {
      localStorage.setItem(STORAGE_KEY, String(enabled));
      debugLog('Saved preference:', enabled);
    } catch (e) {
      debugWarn('localStorage write failed:', e);
    }
  }

  /**
   * Check if Eid intro has been shown this session
   */
  function hasEidIntroShown() {
    try {
      return sessionStorage.getItem(EID_INTRO_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  /**
   * Mark Eid intro as shown
   */
  function markEidIntroShown() {
    try {
      sessionStorage.setItem(EID_INTRO_KEY, 'true');
    } catch (e) {
      // Silent fail
    }
    state.eidIntroShown = true;
  }
  
  /**
   * Detect current page type for scope matching
   */
  function detectPageType() {
    const path = window.location.pathname;
    
    if (path === '/' || path === '/index' || path.includes('/collections/collection-shary-home')) {
      return 'home';
    }
    if (path.includes('/collections/')) {
      return 'collection';
    }
    if (path.includes('/products/')) {
      return 'product';
    }
    if (path.includes('/cart')) {
      return 'cart';
    }
    if (path.includes('/search')) {
      return 'search';
    }
    if (path.includes('/pages/')) {
      return 'page';
    }
    return 'other';
  }
  
  /**
   * Check if scope matches current page
   */
  function scopeMatches(scope) {
    if (!scope || scope === 'all') return true;
    
    const pageType = detectPageType();
    const scopes = scope.split(',').map(s => s.trim().toLowerCase());
    
    return scopes.includes(pageType) || scopes.includes('all');
  }

  function buildAuditSnapshot(extra = {}) {
    const canvasRect = state.canvas ? state.canvas.getBoundingClientRect() : null;
    const canvasCssSize = canvasRect ? `${Math.round(canvasRect.width)}x${Math.round(canvasRect.height)}` : '0x0';
    const canvasInternalSize = state.canvas ? `${state.canvas.width}x${state.canvas.height}` : '0x0';
    const canvasCssOk = canvasRect ? canvasRect.width > 0 && canvasRect.height > 0 : false;
    const canvasInternalOk = state.canvas ? state.canvas.width > 0 && state.canvas.height > 0 : false;
    const overlayZ = state.container ? parseInt(window.getComputedStyle(state.container).zIndex, 10) || 0 : 0;
    const particleCount = Math.max(state.canvasParticles.length, state.particles.length);

    return {
      pageType: detectPageType(),
      scope: state.scope,
      scopeMatched: scopeMatches(state.scope),
      season: state.season,
      intensity: state.intensity,
      localStorage: getStoredPreference(),
      presetName: state.currentPreset ? state.currentPreset.id : state.season,
      nodeCount: state.particles.length,
      particleCount,
      canvasCssSize,
      canvasCssOk,
      canvasInternalSize,
      canvasInternalOk,
      canvasDpr: state.canvasSize.dpr || (window.devicePixelRatio || 1),
      overlayZ,
      loaderFound: auditState.loaderFound,
      overlayAttached: auditState.overlayAttached,
      reducedMotion: prefersReducedMotion(),
      ...extra
    };
  }

  /**
   * Scale particle counts by intensity while honoring density caps
   */
  function getScaledCount(countConfig) {
    const presets = window.SharySeasonalPresets;
    if (!presets) return 0;

    const base = presets.getCountForDevice(countConfig);
    const scaled = Math.max(1, Math.round(base * (state.intensity || 1)));
    const tier = presets.getDeviceTier();
    const limit = (presets.DENSITY && presets.DENSITY[tier] && presets.DENSITY[tier].max) || scaled;
    return Math.min(scaled, limit);
  }

  function getIntensityOpacityFactor() {
    const intensity = state.intensity || 1;
    const factor = 0.6 + 0.2 * intensity;
    return Math.max(0.5, Math.min(1.2, factor));
  }

  // ---------------------------------------------------------------------------
  // CANVAS + RENDER GUARDS
  // ---------------------------------------------------------------------------

  // FIX: Canvas readiness / DPR scaling / resize stability
  const CANVAS_MAX_ATTEMPTS = 10;
  const CANVAS_RETRY_DELAY = 200;
  let resizeScheduled = false;

  function createCanvasLayer(container) {
    const canvas = document.createElement('canvas');
    canvas.className = 'shary-seasonal__canvas';
    canvas.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; pointer-events:none;';
    container.appendChild(canvas);
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      debugWarn('Canvas 2D context unavailable');
      if (AUDIT_MODE) {
        showAuditOverlay('SEASONAL FAIL: Canvas context not available', 'error');
      }
      return;
    }

    state.canvas = canvas;
    state.ctx = ctx;
    state.canvasParticles = [];
    state.canvasSize = { width: 0, height: 0, dpr: 1 };
    state.sizeCheckAttempts = 0;

    const ready = ensureCanvasReady(true, 1);
    if (!ready && AUDIT_MODE) {
      showAuditOverlay('SEASONAL WARNING: Canvas not measurable yet — retrying', 'warning');
    }

    attachResizeHandlers(container);
  }

  function ensureCanvasReady(withRetries = false, attempt = 1) {
    const canvas = state.canvas;
    const ctx = state.ctx;

    if (!canvas || !ctx) return false;

    const rect = canvas.getBoundingClientRect();
    let width = Math.round(rect.width);
    let height = Math.round(rect.height);

    if ((width === 0 || height === 0) && withRetries && attempt <= CANVAS_MAX_ATTEMPTS) {
      state.sizeCheckAttempts = attempt;
      debugWarn('Canvas size is 0px, scheduling retry', { attempt });
      requestAnimationFrame(() => ensureCanvasReady(true, attempt + 1));
      setTimeout(() => ensureCanvasReady(true, attempt + 1), CANVAS_RETRY_DELAY);
      return false;
    }

    if (width === 0 || height === 0) {
      // Fallback to viewport sizing to force visibility
      width = Math.max(window.innerWidth, 1);
      height = Math.max(window.innerHeight, 1);
      canvas.style.position = 'fixed';
      canvas.style.inset = '0';
      canvas.style.width = '100vw';
      canvas.style.height = '100vh';
      debugWarn('Canvas measured 0px, forcing full-viewport fallback');
      if (AUDIT_MODE) {
        showAuditOverlay('SEASONAL WARNING: Canvas was 0px — forced to viewport', 'warning');
      }
    }

    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    const sizeChanged = width !== state.canvasSize.width || height !== state.canvasSize.height || dpr !== state.canvasSize.dpr;

    if (sizeChanged) {
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      state.canvasSize = { width, height, dpr };
      state.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      debugLog('Canvas resized', { width, height, dpr, attempt });
      reseedCanvasParticles();
    }

    return width > 0 && height > 0;
  }

  function attachResizeHandlers(container) {
    const schedule = reason => {
      if (resizeScheduled) return;
      resizeScheduled = true;
      requestAnimationFrame(() => {
        resizeScheduled = false;
        const ready = ensureCanvasReady(false, 1);
        if (DEBUG_MODE || AUDIT_MODE) {
          debugLog('Canvas resize check', { reason, ready, size: state.canvasSize });
        }
      });
    };

    const resizeHandler = () => schedule('window-resize');
    const orientationHandler = () => schedule('orientationchange');
    state.resizeHandler = resizeHandler;
    state.orientationHandler = orientationHandler;
    window.addEventListener('resize', resizeHandler);
    window.addEventListener('orientationchange', orientationHandler);

    if (window.ResizeObserver) {
      state.resizeObserver = new ResizeObserver(() => schedule('resize-observer'));
      state.resizeObserver.observe(container);
    }
  }

  function reseedCanvasParticles() {
    if (!state.currentPreset || !state.canvas) return;
    buildCanvasParticles(state.currentPreset);
  }

  function buildCanvasParticles(preset) {
    if (!state.canvas || !window.SharySeasonalPresets) return;

    const presets = window.SharySeasonalPresets;
    const particles = [];
    const width = state.canvasSize.width || Math.max(window.innerWidth, 1);
    const height = state.canvasSize.height || Math.max(window.innerHeight, 1);

    preset.layers?.forEach(layer => {
      const count = getScaledCount(layer.count);
      for (let i = 0; i < count; i++) {
        particles.push(createCanvasParticle(layer.layer));
      }
    });

    state.canvasParticles = particles;

    if ((DEBUG_MODE || AUDIT_MODE) && particles.length === 0) {
      debugWarn('Canvas particle list is empty after build');
    }
  }

  function createCanvasParticle(layer) {
    const width = state.canvasSize.width || Math.max(window.innerWidth, 1);
    const height = state.canvasSize.height || Math.max(window.innerHeight, 1);
    const season = state.season || 'ramadan';

    if (season === 'winter') {
      const size = layer === 'bg' ? randomInRange(1.5, 2.6) : randomInRange(2.3, 3.4);
      return {
        season,
        layer,
        x: randomInRange(0, width),
        y: randomInRange(0, height),
        r: size,
        vx: randomInRange(-6, 6),
        vy: randomInRange(14, 28),
        alpha: layer === 'bg' ? 0.08 : 0.14
      };
    }

    if (season === 'eid') {
      const size = layer === 'bg' ? randomInRange(2, 3) : randomInRange(3, 4.4);
      return {
        season,
        layer,
        x: randomInRange(0, width),
        y: randomInRange(0, height),
        r: size,
        vx: randomInRange(-2, 2),
        vy: randomInRange(-2, 2),
        alpha: layer === 'bg' ? 0.12 : 0.18,
        twinkle: randomInRange(0.0015, 0.003)
      };
    }

    if (season === 'summer') {
      return {
        season,
        layer,
        x: randomInRange(0, width),
        y: randomInRange(0, height),
        r: randomInRange(40, 80),
        vx: 0,
        vy: 0,
        alpha: 0.05
      };
    }

    if (season === 'blackfriday') {
      return {
        season,
        layer,
        x: randomInRange(0, width),
        y: randomInRange(0, height),
        r: randomInRange(60, 140),
        vx: 0,
        vy: randomInRange(-6, -2),
        alpha: 0.1
      };
    }

    // Default: Ramadan lantern glow
    const lanternSize = layer === 'bg' ? randomInRange(10, 16) : randomInRange(16, 22);
    return {
      season,
      layer,
      x: randomInRange(0, width),
      y: randomInRange(0, height),
      r: lanternSize,
      vx: randomInRange(-4, 4),
      vy: randomInRange(-6, 6),
      sway: randomInRange(6, 14),
      alpha: layer === 'bg' ? 0.18 : 0.24,
      phase: randomInRange(0, Math.PI * 2)
    };
  }

  function drawCanvasParticle(ctx, particle, dt, width, height) {
    const t = dt / 16.67;
    particle.x += (particle.vx || 0) * t;
    particle.y += (particle.vy || 0) * t;

    if (particle.season === 'ramadan') {
      particle.phase = (particle.phase || 0) + 0.0025 * dt;
      particle.x += Math.sin(particle.phase) * (particle.sway || 10) * 0.02;
    }

    if (particle.season === 'eid') {
      particle.phase = (particle.phase || 0) + (particle.twinkle || 0.002) * dt;
      particle.alpha = 0.1 + 0.08 * Math.abs(Math.sin(particle.phase));
    }

    // Wrap to keep particles on-screen
    if (particle.x < -particle.r) particle.x = width + particle.r;
    if (particle.x > width + particle.r) particle.x = -particle.r;
    if (particle.y < -particle.r) particle.y = height + particle.r;
    if (particle.y > height + particle.r) particle.y = -particle.r;

    ctx.save();
  ctx.globalAlpha = Math.min(1, (particle.alpha || 0.15) * getIntensityOpacityFactor());

    if (particle.season === 'winter') {
      const gradient = ctx.createRadialGradient(particle.x, particle.y, 0, particle.x, particle.y, particle.r * 2);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.r * 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    if (particle.season === 'eid') {
      ctx.fillStyle = 'rgba(201, 162, 39, 1)';
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    if (particle.season === 'summer') {
      const gradient = ctx.createLinearGradient(particle.x - particle.r, particle.y - particle.r, particle.x + particle.r, particle.y + particle.r);
      gradient.addColorStop(0, 'rgba(244, 124, 32, 0.05)');
      gradient.addColorStop(1, 'rgba(255, 220, 150, 0.04)');
      ctx.fillStyle = gradient;
      ctx.fillRect(particle.x - particle.r, particle.y - particle.r, particle.r * 2, particle.r * 0.6);
      ctx.restore();
      return;
    }

    if (particle.season === 'blackfriday') {
      const gradient = ctx.createLinearGradient(particle.x, particle.y - particle.r, particle.x, particle.y + particle.r);
      gradient.addColorStop(0, 'rgba(244,124,32,0)');
      gradient.addColorStop(0.5, 'rgba(244,124,32,0.15)');
      gradient.addColorStop(1, 'rgba(244,124,32,0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(particle.x, particle.y - particle.r, 2, particle.r * 2);
      ctx.restore();
      return;
    }

    // Ramadan default draw
    const glowRadius = particle.r * 3.2;
    const gradient = ctx.createRadialGradient(particle.x, particle.y, particle.r * 0.5, particle.x, particle.y, glowRadius);
    gradient.addColorStop(0, 'rgba(201, 162, 39, 0.22)');
    gradient.addColorStop(0.6, 'rgba(244, 124, 32, 0.14)');
    gradient.addColorStop(1, 'rgba(244, 124, 32, 0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(particle.x, particle.y, glowRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(201, 162, 39, 0.6)';
    ctx.beginPath();
    ctx.arc(particle.x, particle.y, particle.r * 0.7, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  function drawCanvasFrame(timestamp) {
    if (!state.canvas || !state.ctx || !state.active || state.paused) {
      state.rafId = requestAnimationFrame(drawCanvasFrame);
      return;
    }

    const ready = ensureCanvasReady(false, 1);
    if (!ready) {
      state.rafId = requestAnimationFrame(drawCanvasFrame);
      return;
    }

    const { width, height } = state.canvasSize;
    const dt = state.lastFrameTime ? Math.min(50, timestamp - state.lastFrameTime) : 16.67;
    state.lastFrameTime = timestamp;

    state.ctx.clearRect(0, 0, width, height);

    if (!state.canvasParticles.length) {
      reseedCanvasParticles();
    }

    for (let i = 0; i < state.canvasParticles.length; i++) {
      drawCanvasParticle(state.ctx, state.canvasParticles[i], dt, width, height);
    }

    state.rafId = requestAnimationFrame(drawCanvasFrame);
  }

  function startCanvasLoop() {
    if (!state.canvas || !state.ctx) return;
    if (state.rafId) return;
    state.lastFrameTime = 0;
    state.rafId = requestAnimationFrame(drawCanvasFrame);
  }

  function stopCanvasLoop() {
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
  }

  // ---------------------------------------------------------------------------
  // ELEMENT CREATION
  // ---------------------------------------------------------------------------

  /**
   * Create snowflake element for winter
   */
  function createSnowflake(layer, preset) {
    const particle = document.createElement('div');
    particle.className = 'shary-seasonal__particle';
    particle.dataset.layer = layer;
    
    // Random position
    particle.style.left = `${randomInRange(0, 100)}%`;
    particle.style.top = `${randomInRange(-10, -5)}%`;
    
    // Random animation delay for staggered effect
    const delay = randomInRange(0, preset.timing[layer === 'bg' ? 'bgDuration' : 'fgDuration']);
    particle.style.animationDelay = `-${delay}ms`;
    
    return particle;
  }

  /**
   * Create lantern element for Ramadan
   */
  function createLantern(layer, preset) {
    const particle = document.createElement('div');
    particle.className = 'shary-seasonal__particle';
    particle.dataset.layer = layer;
    
    // Lantern body
    const lantern = document.createElement('div');
    lantern.className = 'shary-seasonal__lantern';
    
    // Glow halo
    const glow = document.createElement('div');
    glow.className = 'shary-seasonal__glow';
    
    particle.appendChild(glow);
    particle.appendChild(lantern);
    
    // Position
    particle.style.left = `${randomInRange(5, 95)}%`;
    particle.style.top = `${randomInRange(10, 80)}%`;
    
    // Stagger animations
    const delay = randomInRange(0, preset.timing[layer === 'bg' ? 'bgDuration' : 'fgDuration']);
    lantern.style.animationDelay = `-${delay}ms`;
    glow.style.animationDelay = `-${delay * 0.7}ms`;
    
    return particle;
  }

  /**
   * Create sparkle element for Eid
   */
  function createSparkle(layer, preset) {
    const sparkle = document.createElement('div');
    sparkle.className = 'shary-seasonal__sparkle';
    sparkle.dataset.layer = layer;
    
    // Random position across entire viewport
    sparkle.style.left = `${randomInRange(5, 95)}%`;
    sparkle.style.top = `${randomInRange(5, 95)}%`;
    
    // Stagger animation
    const delay = randomInRange(0, preset.timing[layer === 'bg' ? 'bgDuration' : 'fgDuration']);
    sparkle.style.animationDelay = `-${delay}ms`;
    
    return sparkle;
  }

  /**
   * Create confetti piece for Eid intro
   */
  function createConfetti(index, total) {
    const confetti = document.createElement('div');
    confetti.className = 'shary-seasonal__confetti';
    
    // Spread across viewport
    confetti.style.left = `${randomInRange(10, 90)}%`;
    confetti.style.top = '-20px';
    
    // Randomize fall direction
    const xDrift = randomInRange(-100, 100);
    const rotation = randomInRange(360, 1080);
    confetti.style.setProperty('--confetti-x', `${xDrift}px`);
    confetti.style.setProperty('--confetti-rotate', `${rotation}deg`);
    
    // Stagger spawn
    confetti.style.animationDelay = `${index * 80}ms`;
    
    return confetti;
  }

  /**
   * Create light ray for summer
   */
  function createRay(index) {
    const ray = document.createElement('div');
    ray.className = 'shary-seasonal__ray';
    
    if (index > 0) {
      ray.style.animationDelay = '-14s';
    }
    
    return ray;
  }

  /**
   * Create shimmer for summer
   */
  function createShimmer() {
    const shimmer = document.createElement('div');
    shimmer.className = 'shary-seasonal__shimmer';
    return shimmer;
  }

  /**
   * Create streak for Black Friday
   */
  function createStreak(index, total) {
    const streak = document.createElement('div');
    streak.className = 'shary-seasonal__streak';
    
    // Distribute across viewport width
    streak.style.left = `${(index / total) * 100 + randomInRange(-5, 5)}%`;
    
    // Stagger animation
    streak.style.animationDelay = `-${index * 2}s`;
    
    return streak;
  }

  // ---------------------------------------------------------------------------
  // AUDIT HELPERS — PHASE 2 & 5
  // ---------------------------------------------------------------------------
  
  /**
   * PHASE 2: Create force visibility overlay
   * Full-screen red tint with text to prove overlay is attached and visible
   */
  function createForceVisibilityOverlay(container) {
    const forceOverlay = document.createElement('div');
    forceOverlay.className = 'shary-seasonal__force-overlay';
    forceOverlay.style.cssText = `
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      background: rgba(255, 0, 0, 0.08) !important;
      border: 4px solid red !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      pointer-events: none !important;
      z-index: 1 !important;
    `;
    
    const label = document.createElement('div');
    label.style.cssText = `
      background: rgba(255, 0, 0, 0.9) !important;
      color: white !important;
      padding: 10px 20px !important;
      border-radius: 8px !important;
      font-size: 16px !important;
      font-weight: bold !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
      pointer-events: none !important;
    `;
    label.textContent = '🔴 SEASONAL OVERLAY ACTIVE';
    
    forceOverlay.appendChild(label);
    container.appendChild(forceOverlay);
    
    debugLog('Force visibility overlay created');
  }
  
  /**
   * PHASE 5: Create giant proof square
   * If this is NOT visible, the preset is NOT running
   */
  function createProofSquare(container) {
    const square = document.createElement('div');
    square.className = 'shary-seasonal__proof-square';
    square.style.cssText = `
      width: 200px !important;
      height: 200px !important;
      background: var(--shary-warning) !important;
      position: fixed !important;
      top: 100px !important;
      left: 100px !important;
      z-index: 2000 !important;
      pointer-events: auto !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 14px !important;
      font-weight: bold !important;
      color: var(--shary-text-primary) !important;
      text-align: center !important;
      padding: 10px !important;
      box-sizing: border-box !important;
      box-shadow: var(--shary-card-shadow) !important;
      border: 4px solid var(--shary-warning) !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
    `;
    square.innerHTML = '🏆 PROOF SQUARE<br>Ramadan preset IS running<br><small>Click to dismiss</small>';
    
    square.addEventListener('click', () => {
      square.remove();
      debugLog('Proof square dismissed');
    });
    
    // Append to body directly for maximum z-index reliability
    document.body.appendChild(square);
    state.particles.push(square);
    
    debugLog('Proof square created — if NOT visible, stacking/CSS is broken');
  }

  // ---------------------------------------------------------------------------
  // SEASON RENDERERS
  // ---------------------------------------------------------------------------

  /**
   * Render winter season
   */
  function renderWinter(preset, container) {
    const presets = window.SharySeasonalPresets;
    
    // AUDIT MODE: Add force visibility test overlay (PHASE 2)
    if (AUDIT_MODE) {
      createForceVisibilityOverlay(container);
    }
    
    preset.layers.forEach(layer => {
      const count = getScaledCount(layer.count);
      
      for (let i = 0; i < count; i++) {
        // Gradual spawn
        setTimeout(() => {
          if (!state.active) return;
          
          const particle = createSnowflake(layer.layer, preset);
          container.appendChild(particle);
          state.particles.push(particle);
        }, i * preset.timing.spawnDelay);
      }
    });
  }

  /**
   * Render Ramadan season
   */
  function renderRamadan(preset, container) {
    const presets = window.SharySeasonalPresets;
    
    // AUDIT MODE: Add force visibility test overlay (PHASE 2)
    if (AUDIT_MODE) {
      createForceVisibilityOverlay(container);
    }
    
    // AUDIT MODE (PHASE 5): Create giant visible proof square
    if (AUDIT_MODE) {
      createProofSquare(container);
    }
    
    preset.layers.forEach(layer => {
      const count = getScaledCount(layer.count);
      
      for (let i = 0; i < count; i++) {
        setTimeout(() => {
          if (!state.active) return;
          
          const particle = createLantern(layer.layer, preset);
          container.appendChild(particle);
          state.particles.push(particle);
        }, i * preset.timing.spawnDelay);
      }
    });
  }

  /**
   * Render Eid season
   */
  function renderEid(preset, container) {
    const presets = window.SharySeasonalPresets;
    
    // AUDIT MODE: Add force visibility test overlay (PHASE 2)
    if (AUDIT_MODE) {
      createForceVisibilityOverlay(container);
    }
    
    // Check for intro (first load only)
    if (preset.intro && preset.intro.enabled && !hasEidIntroShown()) {
      const confettiCount = getScaledCount(preset.intro.confettiCount);
      
      for (let i = 0; i < confettiCount; i++) {
        const confetti = createConfetti(i, confettiCount);
        container.appendChild(confetti);
        
        // Remove after animation completes
        setTimeout(() => {
          if (confetti.parentNode) {
            confetti.remove();
          }
        }, preset.intro.duration + 500);
      }
      
      markEidIntroShown();
    }
    
    // Persistent sparkles
    preset.layers.forEach(layer => {
      const count = getScaledCount(layer.count);
      
      for (let i = 0; i < count; i++) {
        setTimeout(() => {
          if (!state.active) return;
          
          const sparkle = createSparkle(layer.layer, preset);
          container.appendChild(sparkle);
          state.particles.push(sparkle);
        }, i * preset.timing.spawnDelay);
      }
    });
  }

  /**
   * Render summer season
   */
  function renderSummer(preset, container) {
    const presets = window.SharySeasonalPresets;
    
    // AUDIT MODE: Add force visibility test overlay (PHASE 2)
    if (AUDIT_MODE) {
      createForceVisibilityOverlay(container);
    }
    
    // Light rays
    const rayCount = getScaledCount(preset.layers[0].count);
    for (let i = 0; i < rayCount; i++) {
      const ray = createRay(i);
      container.appendChild(ray);
      state.particles.push(ray);
    }
    
    // Shimmer
    const shimmer = createShimmer();
    container.appendChild(shimmer);
    state.particles.push(shimmer);
  }

  /**
   * Render Black Friday season
   */
  function renderBlackFriday(preset, container) {
    const presets = window.SharySeasonalPresets;
    
    // AUDIT MODE: Add force visibility test overlay (PHASE 2)
    if (AUDIT_MODE) {
      createForceVisibilityOverlay(container);
    }
    
    const streakCount = getScaledCount(preset.layers[0].count);
    
    for (let i = 0; i < streakCount; i++) {
      const streak = createStreak(i, streakCount);
      container.appendChild(streak);
      state.particles.push(streak);
    }
  }

  // ---------------------------------------------------------------------------
  // DEBUG PANEL (Only when ?debugSeasonal=1)
  // ---------------------------------------------------------------------------
  
  function createDebugPanel() {
    if (!DEBUG_MODE) return null;
    
    const panel = document.createElement('div');
    panel.id = 'shary-seasonal-debug';
    panel.style.cssText = `
      position: fixed;
      top: 60px;
      right: 10px;
      z-index: 99999;
      background: color-mix(in srgb, var(--shary-text-primary) 92%, transparent);
      color: var(--shary-success);
      font-family: monospace;
      font-size: 11px;
      padding: 12px;
      border-radius: 8px;
      max-width: 280px;
      min-width: 240px;
      direction: rtl;
      text-align: right;
      box-shadow: var(--shary-card-shadow);
      border: 1px solid var(--shary-divider-light);
    `;
    
    const title = document.createElement('div');
    title.style.cssText = 'font-size: 13px; font-weight: bold; margin-bottom: 10px; color: var(--shary-orange); border-bottom: 1px solid color-mix(in srgb, var(--shary-card-bg) 20%, transparent); padding-bottom: 8px;';
    title.textContent = 'تصحيح المؤثرات 🔧';
    panel.appendChild(title);
    
    const content = document.createElement('div');
    content.id = 'shary-debug-content';
    content.style.cssText = 'line-height: 1.6;';
    panel.appendChild(content);
    
    // Controls section
    const controls = document.createElement('div');
    controls.style.cssText = 'margin-top: 12px; padding-top: 10px; border-top: 1px solid color-mix(in srgb, var(--shary-card-bg) 20%, transparent); display: flex; flex-wrap: wrap; gap: 6px;';
    
    // Reset button
    const resetBtn = document.createElement('button');
    resetBtn.textContent = '🔄 Reset';
    resetBtn.style.cssText = 'background: var(--shary-error); color: var(--shary-card-bg); border: none; padding: 6px 10px; border-radius: 4px; cursor: pointer; font-size: 10px;';
    resetBtn.addEventListener('click', () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
        debugLog('localStorage cleared');
        window.location.reload();
      } catch (e) {
        debugError('Reset failed:', e);
      }
    });
    controls.appendChild(resetBtn);
    
    // Boost visibility button
    const boostBtn = document.createElement('button');
    boostBtn.textContent = '✨ Boost';
    boostBtn.style.cssText = 'background: var(--shary-orange); color: var(--shary-card-bg); border: none; padding: 6px 10px; border-radius: 4px; cursor: pointer; font-size: 10px;';
    boostBtn.addEventListener('click', () => {
      state.boostMode = !state.boostMode;
      boostBtn.style.background = state.boostMode ? 'var(--shary-success)' : 'var(--shary-orange)';
      applyBoostMode(state.boostMode);
      updateDebugPanel();
    });
    controls.appendChild(boostBtn);
    
    // Force start button
    const forceBtn = document.createElement('button');
    forceBtn.textContent = '▶️ Force';
    forceBtn.style.cssText = 'background: var(--shary-info); color: var(--shary-card-bg); border: none; padding: 6px 10px; border-radius: 4px; cursor: pointer; font-size: 10px;';
    forceBtn.addEventListener('click', forceStart);
    controls.appendChild(forceBtn);
    
    panel.appendChild(controls);
    
    return panel;
  }
  
  function applyBoostMode(enabled) {
    if (!state.container) return;
    
    if (enabled) {
      // Temporarily boost visibility for debugging
      state.container.style.cssText += `
        opacity: 1 !important;
        visibility: visible !important;
      `;
      state.particles.forEach(p => {
        p.style.opacity = '0.5';
      });
      debugLog('Boost mode enabled - particles opacity set to 50%');
    } else {
      // Restore normal
      state.container.style.opacity = '';
      state.container.style.visibility = '';
      state.particles.forEach(p => {
        p.style.opacity = '';
      });
      debugLog('Boost mode disabled');
    }
  }
  
  function forceStart() {
    debugLog('Force starting effects...');
    
    // If not initialized, try to init
    if (!state.initialized) {
      const loader = document.querySelector('[data-shary-seasonal-loader]');
      if (loader) {
        const config = {
          season: loader.dataset.sharySeason || 'ramadan',
          intensity: parseInt(loader.dataset.sharyIntensity, 10) || 2,
          forceStart: true
        };
        init(config);
      } else {
        // Create minimal config
        const config = {
          season: 'ramadan',
          intensity: 2,
          forceStart: true
        };
        init(config);
      }
    } else {
      // Already initialized, just enable
      state.active = true;
      savePreference(true);
      if (state.container) {
        state.container.dataset.active = 'true';
      }
      resumeAnimations();
    }
    
    updateDebugPanel();
  }
  
  function updateDebugPanel() {
    if (!DEBUG_MODE || !state.debugPanel) return;
    
    const content = state.debugPanel.querySelector('#shary-debug-content');
    if (!content) return;
    
    const loader = document.querySelector('[data-shary-seasonal-loader]');
    const overlay = document.querySelector('.shary-seasonal');
    const toggle = document.querySelector('.shary-seasonal-toggle');
    
    let overlayZIndex = 'N/A';
    if (overlay) {
      overlayZIndex = window.getComputedStyle(overlay).zIndex;
    }
    
    let storedValue = 'N/A';
    try {
      storedValue = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      storedValue = 'blocked';
    }
    
    const pageType = detectPageType();
    const reducedMotion = prefersReducedMotion();
    
    const presets = window.SharySeasonalPresets;
    const presetLoaded = presets ? Object.keys(presets.PRESETS || {}).join(', ') : 'NOT LOADED';
    
    const items = [
      { label: 'Theme Enabled', value: state.themeEnabled, ok: state.themeEnabled },
      { label: 'Season', value: state.season || (loader?.dataset.sharySeason) || 'none', ok: !!state.season },
      { label: 'Intensity', value: state.intensity },
      { label: 'localStorage', value: storedValue, ok: storedValue === 'true' || storedValue === null },
      { label: 'Active (toggle)', value: state.active, ok: state.active },
      { label: 'Reduced Motion', value: reducedMotion, ok: !reducedMotion },
      { label: 'Page Type', value: pageType },
      { label: 'Scope Match', value: scopeMatches(state.scope), ok: scopeMatches(state.scope) },
      { label: 'Loader Exists', value: !!loader, ok: !!loader },
      { label: 'Overlay Exists', value: !!overlay, ok: !!overlay },
      { label: 'Overlay z-index', value: overlayZIndex, ok: parseInt(overlayZIndex, 10) >= 50 },
      { label: 'Toggle Exists', value: !!toggle, ok: !!toggle },
      { label: 'Presets Loaded', value: presetLoaded, ok: !!presets },
      { label: 'Canvas Size', value: state.canvas ? `${state.canvasSize.width}x${state.canvasSize.height}` : 'none', ok: state.canvasSize.width > 0 && state.canvasSize.height > 0 },
      { label: 'Canvas DPR', value: state.canvasSize.dpr || 1 },
      { label: 'RAF Active', value: !!state.rafId, ok: !!state.rafId || !state.active },
      { label: 'Particle Count', value: state.particles.length },
      { label: 'Initialized', value: state.initialized, ok: state.initialized },
      { label: 'Boost Mode', value: state.boostMode }
    ];
    
    content.innerHTML = items.map(item => {
      const color = item.ok === undefined ? 'var(--shary-card-bg)' : (item.ok ? 'var(--shary-success)' : 'var(--shary-error)');
      const icon = item.ok === undefined ? '' : (item.ok ? '✓' : '✗');
      return `<div style="color: ${color}; margin: 2px 0;">${icon} ${item.label}: <strong>${item.value}</strong></div>`;
    }).join('');
    
    // Add warnings
    const warnings = [];
    if (reducedMotion) {
      warnings.push('⚠️ prefers-reduced-motion: reduce — المؤثرات معطلة');
    }
    if (storedValue === 'false') {
      warnings.push('⚠️ المستخدم أوقف المؤثرات — اضغط Reset');
    }
    if (!loader) {
      warnings.push('⚠️ Loader غير موجود — المؤثرات غير مفعلة من الثيم');
    }
    if (!overlay) {
      warnings.push('⚠️ Overlay غير موجود — اضغط Force');
    }
    if (parseInt(overlayZIndex, 10) < 50) {
      warnings.push('⚠️ z-index منخفض — المؤثرات مخفية');
    }
    if (!state.canvas) {
      warnings.push('⚠️ طبقة الكانفس غير موجودة — أعد التحميل');
    }
    if (state.canvas && (state.canvasSize.width === 0 || state.canvasSize.height === 0)) {
      warnings.push('⚠️ قياس الكانفس صفر — تم تفعيل وضع الطوارئ');
    }
    if (state.active && state.canvas && !state.rafId) {
      warnings.push('⚠️ حلقة الرسم غير نشطة — تحقق من raf');
    }
    if (state.particles.length === 0 && state.initialized) {
      warnings.push('⚠️ لا توجد particles — تحقق من الموسم');
    }
    
    if (warnings.length > 0) {
      content.innerHTML += `<div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid color-mix(in srgb, var(--shary-card-bg) 20%, transparent); color: var(--shary-warning);">
        ${warnings.join('<br>')}
      </div>`;
    }
  }

  // ---------------------------------------------------------------------------
  // TOGGLE BUTTON
  // ---------------------------------------------------------------------------

  /**
   * Create toggle button
   */
  function createToggleButton(preset) {
    const btn = document.createElement('button');
    btn.className = 'shary-seasonal-toggle';
    btn.type = 'button';
    btn.setAttribute('aria-label', TOOLTIP_TEXT);
    btn.setAttribute('aria-pressed', String(state.active));
    btn.dataset.tooltip = TOOLTIP_TEXT;
    
    // Season-specific icon
    btn.innerHTML = preset.icon || window.SharySeasonalPresets.ICONS.default;
    
    // Click handler
    btn.addEventListener('click', toggleEffects);
    
    return btn;
  }

  /**
   * Toggle effects on/off
   */
  function toggleEffects() {
    debugLog('Toggle clicked, current state:', state.active);
    state.active = !state.active;
    savePreference(state.active);
    
    if (state.container) {
      state.container.dataset.active = String(state.active);
    }
    
    if (state.toggleBtn) {
      state.toggleBtn.setAttribute('aria-pressed', String(state.active));
    }
    
    if (state.active) {
      resumeAnimations();
    } else {
      pauseAnimations();
    }
    
    // Update debug panel
    updateDebugPanel();
  }

  /**
   * Pause all animations
   */
  function pauseAnimations() {
    debugLog('Pausing animations');
    if (state.container) {
      state.container.style.animationPlayState = 'paused';
      state.particles.forEach(p => {
        p.style.animationPlayState = 'paused';
      });
    }
    stopCanvasLoop();
  }

  /**
   * Resume all animations
   */
  function resumeAnimations() {
    debugLog('Resuming animations');
    if (state.container) {
      state.container.style.animationPlayState = 'running';
      state.particles.forEach(p => {
        p.style.animationPlayState = 'running';
      });
    }
    startCanvasLoop();
  }

  // ---------------------------------------------------------------------------
  // VISIBILITY HANDLING
  // ---------------------------------------------------------------------------

  /**
   * Handle document visibility change
   */
  function handleVisibilityChange() {
    if (document.hidden) {
      state.paused = true;
      pauseAnimations();
    } else if (state.active) {
      state.paused = false;
      resumeAnimations();
    }
  }

  // ---------------------------------------------------------------------------
  // MAIN INITIALIZATION (AUDITED — WITH HARD ASSERTIONS)
  // ---------------------------------------------------------------------------

  /**
   * Initialize the seasonal engine
   * @param {Object} config - Configuration from data attributes
   */
  function init(config) {
    debugLog('init() called with config:', config);
    
    // AUDIT: Track initialization for trace panel
    auditState.presetInitCalled = true;
    
    // Guard: already initialized (unless force start)
    if (state.initialized && !config.forceStart) {
      debugLog('Already initialized, skipping');
      return;
    }
    
    // Store theme enabled state for debug
    state.themeEnabled = true;
    state.scope = config.scope || 'all';
    state.style = config.style || 'default';
    state.season = config.season || state.season;
    state.intensity = config.intensity || state.intensity;
    
    // CRITICAL FIX: Check scope BEFORE initializing
    // This was missing — scope was checked only for debug panel display
    const pageType = detectPageType();
    const scopeMatched = scopeMatches(state.scope);
    auditState.scopeMatched = scopeMatched;
    
    if (!scopeMatched && !config.forceStart) {
      debugLog('Scope does not match current page. Page:', pageType, 'Scope:', state.scope);
      console.log(`[Shary Seasonal] Skipped — scope "${state.scope}" does not include page type "${pageType}"`);
      
      // AUDIT MODE: Show visible warning if scope doesn't match
      if (AUDIT_MODE) {
        showTracePanel(buildAuditSnapshot({
          scopeMatched: false,
          season: config.season,
          intensity: config.intensity,
          presetName: config.season,
          nodeCount: 0,
          particleCount: 0,
          canvasCssSize: '0x0',
          canvasCssOk: false,
          canvasInternalSize: '0x0',
          canvasInternalOk: false,
          overlayZ: 0,
          overlayAttached: false
        }));
        showAuditOverlay(`Scope mismatch: Page is "${pageType}" but scope is set to "${state.scope}"`, 'warning');
      }
      return;
    }
    
    // Guard: reduced motion preference (skip if force start in debug mode)
    if (prefersReducedMotion() && !config.forceStart) {
      debugLog('Disabled due to prefers-reduced-motion');
      auditState.reducedMotionActive = true;
      
      // AUDIT MODE: Show yellow overlay for reduced motion
      if (AUDIT_MODE) {
        showAuditOverlay('SEASONAL DISABLED: prefers-reduced-motion is active on this device', 'warning');
        showTracePanel(buildAuditSnapshot({
          season: config.season,
          intensity: config.intensity,
          presetName: config.season,
          nodeCount: 0,
          particleCount: 0,
          canvasCssSize: '0x0',
          canvasCssOk: false,
          canvasInternalSize: '0x0',
          canvasInternalOk: false,
          overlayAttached: false
        }));
      }
      
      // Always warn in console so users know why effects are disabled
      console.warn('[Shary Seasonal] المؤثرات معطلة — prefers-reduced-motion مفعل على الجهاز');
      // Still create debug panel if in debug mode
      if (DEBUG_MODE) {
        state.debugPanel = createDebugPanel();
        if (state.debugPanel) {
          document.body.appendChild(state.debugPanel);
          updateDebugPanel();
        }
      }
      return;
    }
    
    // Guard: presets not loaded
    if (!window.SharySeasonalPresets) {
      debugWarn('Presets module not loaded');
      
      // AUDIT MODE: Show hard failure
      if (AUDIT_MODE) {
        showAuditOverlay('SEASONAL FAIL: Presets module not loaded (shary-seasonal-presets.js)', 'error');
        showTracePanel(buildAuditSnapshot({
          season: config.season,
          intensity: config.intensity,
          presetName: config.season,
          nodeCount: 0,
          particleCount: 0,
          canvasCssSize: '0x0',
          canvasCssOk: false,
          canvasInternalSize: '0x0',
          canvasInternalOk: false,
          overlayAttached: false
        }));
      }
      
      console.warn('[Shary Seasonal] Presets module not loaded');
      return;
    }
    
    const presets = window.SharySeasonalPresets;
    const { PRESETS } = presets;
    
    debugLog('Available presets:', Object.keys(PRESETS));
    
    // Get configuration
    const season = config.season || 'winter';
    const preset = PRESETS[season];
    
    if (!preset) {
      debugWarn('Unknown season:', season);
      
      // AUDIT MODE: Show hard failure
      if (AUDIT_MODE) {
        showAuditOverlay(`SEASONAL FAIL: Unknown season "${season}"`, 'error');
        showTracePanel(buildAuditSnapshot({
          season,
          intensity: config.intensity,
          presetName: season,
          nodeCount: 0,
          particleCount: 0,
          canvasCssSize: '0x0',
          canvasCssOk: false,
          canvasInternalSize: '0x0',
          canvasInternalOk: false,
          overlayAttached: false
        }));
      }
      
      console.warn(`[Shary Seasonal] Unknown season: ${season}`);
      return;
    }
    
    debugLog('Using preset:', season, preset);
    
    // Determine intensity (force 1 for low-end devices)
    state.intensity = presets.determineIntensity(config.intensity);
    state.season = season;
    state.active = loadPreference();
    
    debugLog('State after config:', { intensity: state.intensity, season: state.season, active: state.active });
    
    // Create container
    const container = document.createElement('div');
    container.className = `shary-seasonal shary-seasonal--${season}`;
    container.id = 'shary-seasonal-overlay';
    container.dataset.sharySeasonal = season;
    container.dataset.intensity = String(state.intensity);
    container.dataset.active = String(state.active);
    container.setAttribute('aria-hidden', 'true');
    
    // Add debug attribute if in debug mode
    if (DEBUG_MODE) {
      container.dataset.debug = 'true';
    }
    
    state.container = container;
    state.currentPreset = preset;
    
    // Attach container before rendering to guarantee measurable layout for canvas
    document.body.appendChild(container);
    debugLog('Container appended to body');
    
    // AUDIT: Verify overlay is actually attached to body
    auditState.overlayAttached = container.parentNode === document.body;
    if (!auditState.overlayAttached && AUDIT_MODE) {
      showAuditOverlay('SEASONAL FAIL: Overlay not attached to document.body', 'error');
      return;
    }
    
    // FIX: Canvas readiness / DPR scaling / resize stability
    createCanvasLayer(container);
    buildCanvasParticles(preset);
    startCanvasLoop();
    
    // Render season
    try {
      debugLog('Rendering season:', season);
      switch (season) {
        case 'winter':
          renderWinter(preset, container);
          break;
        case 'ramadan':
          renderRamadan(preset, container);
          break;
        case 'eid':
          renderEid(preset, container);
          break;
        case 'summer':
          renderSummer(preset, container);
          break;
        case 'blackfriday':
          renderBlackFriday(preset, container);
          break;
        default:
          debugWarn('No renderer for:', season);
          console.warn(`[Shary Seasonal] No renderer for: ${season}`);
      }
      debugLog('Render complete, particles:', state.particles.length);
    } catch (err) {
      debugError('Render error:', err);
      console.error('[Shary Seasonal] Render error:', err);
      if (AUDIT_MODE) {
        showTracePanel(buildAuditSnapshot());
      }
      return;
    }
    
    // Log computed z-index
    const computedZIndex = window.getComputedStyle(container).zIndex;
    debugLog('Container computed z-index:', computedZIndex);
    
    // AUDIT: Verify z-index is sufficient
    if (AUDIT_MODE && parseInt(computedZIndex, 10) < 50) {
      showAuditOverlay(`SEASONAL FAIL: z-index is ${computedZIndex} (must be >= 50)`, 'error');
    }
    
    // Create toggle button
    state.toggleBtn = createToggleButton(preset);
    document.body.appendChild(state.toggleBtn);
    debugLog('Toggle button created');
    
    // Setup visibility handler
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Initial state
    if (!state.active) {
      pauseAnimations();
    }
    
    // AUDIT: Mark preset start called
    auditState.presetStartCalled = true;
    
    state.initialized = true;
    debugLog('Initialization complete');
    console.log(`[Shary Seasonal] Initialized: ${season} (intensity: ${state.intensity})`);
    
    // Create debug panel if in debug mode
    if (DEBUG_MODE) {
      state.debugPanel = createDebugPanel();
      if (state.debugPanel) {
        document.body.appendChild(state.debugPanel);
        updateDebugPanel();
      }
    }
    
    // AUDIT MODE: Show trace panel with all values
    if (AUDIT_MODE) {
      // Wait a tick for particles to spawn
      setTimeout(() => {
        showTracePanel(buildAuditSnapshot({
          presetName: season,
          intensity: state.intensity,
          overlayAttached: auditState.overlayAttached
        }));
        
        // Show success if all good
        if (state.particles.length > 0) {
          showAuditOverlay(`SEASONAL OK: ${state.particles.length} particles created for "${season}"`, 'success');
        } else {
          showAuditOverlay(`SEASONAL WARNING: 0 particles created — check preset "${season}"`, 'warning');
        }
      }, 1000); // Wait for gradual spawn
    }
  }

  /**
   * Destroy and cleanup
   */
  function destroy() {
    debugLog('destroy() called');

    // Remove any stray nodes (audit proof square, etc.) even if container is gone
    state.particles.forEach(node => {
      if (node && node.parentNode) {
        node.remove();
      }
    });
    
    if (state.container && state.container.parentNode) {
      state.container.remove();
    }
    
    if (state.toggleBtn && state.toggleBtn.parentNode) {
      state.toggleBtn.remove();
    }
    
    if (state.debugPanel && state.debugPanel.parentNode) {
      state.debugPanel.remove();
    }
    
    stopCanvasLoop();
    if (state.resizeObserver) {
      state.resizeObserver.disconnect();
    }
    if (state.resizeHandler) {
      window.removeEventListener('resize', state.resizeHandler);
    }
    if (state.orientationHandler) {
      window.removeEventListener('orientationchange', state.orientationHandler);
    }
    
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    
    state = {
      initialized: false,
      active: true,
      season: null,
      intensity: 2,
      container: null,
      toggleBtn: null,
      particles: [],
      rafId: null,
      paused: false,
      eidIntroShown: false,
      debugPanel: null,
      boostMode: false,
      currentPreset: null,
      themeEnabled: false,
      scope: 'all',
      style: 'default',
      canvas: null,
      ctx: null,
      canvasParticles: [],
      canvasSize: { width: 0, height: 0, dpr: 1 },
      resizeObserver: null,
      sizeCheckAttempts: 0,
      lastFrameTime: 0,
      resizeHandler: null,
      orientationHandler: null
    };
  }

  // ---------------------------------------------------------------------------
  // AUTO-INIT FROM DATA ATTRIBUTES (AUDITED)
  // ---------------------------------------------------------------------------

  function autoInit() {
    debugLog('autoInit() called');
    
    const loader = document.querySelector('[data-shary-seasonal-loader]');
    
    if (!loader) {
      debugLog('No loader found — seasonal effects not enabled in theme');
      auditState.loaderFound = false;
      
      // AUDIT MODE: Show hard failure for missing loader
      if (AUDIT_MODE) {
        showAuditOverlay('SEASONAL FAIL: Loader element not found. Check settings.shary_seasonal_enabled in theme settings.', 'error');
        showTracePanel(buildAuditSnapshot({
          loaderFound: false,
          scopeMatched: false,
          nodeCount: 0,
          particleCount: 0,
          canvasCssSize: '0x0',
          canvasCssOk: false,
          canvasInternalSize: '0x0',
          canvasInternalOk: false,
          overlayAttached: false
        }));
      }
      
      // Still create debug panel if in debug mode
      if (DEBUG_MODE) {
        state.themeEnabled = false;
        state.debugPanel = createDebugPanel();
        if (state.debugPanel) {
          document.body.appendChild(state.debugPanel);
          updateDebugPanel();
        }
      }
      return;
    }
    
    auditState.loaderFound = true;
    debugLog('Loader found:', loader.dataset);
    state.themeEnabled = true;
    
    const config = {
      season: loader.dataset.sharySeason || 'winter',
      intensity: parseInt(loader.dataset.sharyIntensity, 10) || 2,
      scope: loader.dataset.sharyScope || 'all'
    };
    
    debugLog('Config from loader:', config);
    
    // Wait for presets to be available
    if (window.SharySeasonalPresets) {
      debugLog('Presets already loaded, initializing...');
      init(config);
    } else {
      debugLog('Waiting for presets...');
      // Presets should load first, but fallback with short delay
      setTimeout(() => {
        if (!window.SharySeasonalPresets && AUDIT_MODE) {
          showAuditOverlay('SEASONAL FAIL: Presets not loaded after 100ms timeout', 'error');
          showTracePanel(buildAuditSnapshot({
            season: config.season,
            intensity: config.intensity,
            presetName: config.season,
            nodeCount: 0,
            particleCount: 0,
            canvasCssSize: '0x0',
            canvasCssOk: false,
            canvasInternalSize: '0x0',
            canvasInternalOk: false,
            overlayAttached: false
          }));
          return;
        }
        debugLog('Presets ready, initializing...');
        init(config);
      }, 100);
    }
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }

  // Export for manual control
  window.SharySeasonalEngine = {
    init,
    destroy,
    toggle: toggleEffects,
    getState: () => ({ ...state }),
    forceStart: forceStart,
    updateDebugPanel: updateDebugPanel
  };

  // HOW TO VERIFY (AUDIT)
  // - Load homepage with ?auditSeasonal=1
  // - Confirm canvas size > 0 and DPR > 0 in debug panel
  // - Confirm particles visible on iOS, Android, and desktop browsers
  // - Confirm toggle works and interactions are not blocked
  // - Confirm prefers-reduced-motion still disables visuals

})();
